import { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiFetch } from "@/utils/api";
import { PERMISSION_GROUPS, PERMISSION_LABELS } from "@/utils/permission-catalog";

interface Role {
  id: string;
  name: string;
  label: string;
  isSystem: boolean;
  isEditable: boolean;
  permissions: string[];
}

function PermissionPicker({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter((p) => p !== id) : [...selected, id]);
  };

  const toggleGroup = (ids: string[]) => {
    const allOn = ids.every((id) => selected.includes(id));
    onChange(allOn ? selected.filter((p) => !ids.includes(p)) : [...new Set([...selected, ...ids])]);
  };

  return (
    <div className="space-y-4">
      {PERMISSION_GROUPS.map((group) => {
        const ids = group.permissions.map((p) => p.id);
        const allOn = ids.every((id) => selected.includes(id));
        return (
          <div key={group.id} className="rounded-lg border border-[#1e3a4f]/10 p-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-[#1e3a4f]">{group.label}</h4>
              <button
                type="button"
                className="text-xs text-[#1e3a4f]/70 underline hover:text-[#1e3a4f]"
                onClick={() => toggleGroup(ids)}
              >
                {allOn ? "Clear all" : "Select all"}
              </button>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {group.permissions.map((perm) => (
                <label key={perm.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.includes(perm.id)}
                    onChange={() => toggle(perm.id)}
                    className="rounded border-[#1e3a4f]/30"
                  />
                  {perm.label}
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RoleEditor({
  initialLabel,
  initialPermissions,
  labelEditable,
  saving,
  onSave,
  onCancel,
}: {
  initialLabel: string;
  initialPermissions: string[];
  labelEditable: boolean;
  saving: boolean;
  onSave: (label: string, permissions: string[]) => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState(initialLabel);
  const [permissions, setPermissions] = useState(initialPermissions);

  return (
    <div className="space-y-4">
      {labelEditable && (
        <div className="space-y-1.5 max-w-sm">
          <Label htmlFor="role-label">Role name</Label>
          <Input
            id="role-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Field technician"
          />
        </div>
      )}
      <PermissionPicker selected={permissions} onChange={setPermissions} />
      <div className="flex gap-2">
        <Button
          className="bg-[#1e3a4f] hover:bg-[#163041]"
          disabled={saving || (labelEditable && !label.trim()) || permissions.length === 0}
          onClick={() => onSave(labelEditable ? label.trim() : initialLabel, permissions)}
        >
          {saving ? "Saving…" : "Save role"}
        </Button>
        <Button variant="secondary" disabled={saving} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function AdminRolesPage() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newPermissions, setNewPermissions] = useState<string[]>([
    "dashboard_access",
    "systems_menu_access",
  ]);

  const load = async () => {
    const data = await apiFetch<{ roles: Role[] }>("/admin/roles");
    setRoles(data.roles);
  };

  useEffect(() => {
    load()
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const createRole = async () => {
    setSaving(true);
    setError("");
    try {
      await apiFetch("/admin/roles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label: newLabel, permissions: newPermissions }),
      });
      setCreating(false);
      setNewLabel("");
      setNewPermissions(["dashboard_access", "systems_menu_access"]);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create role");
    } finally {
      setSaving(false);
    }
  };

  const updateRole = async (roleId: string, label: string, permissions: string[]) => {
    setSaving(true);
    setError("");
    try {
      await apiFetch(`/admin/roles/${roleId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label, permissions }),
      });
      setEditingId(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update role");
    } finally {
      setSaving(false);
    }
  };

  const deleteRole = async (roleId: string) => {
    if (!window.confirm("Delete this role? Reassign any users first.")) return;
    setError("");
    try {
      await apiFetch(`/admin/roles/${roleId}`, { method: "DELETE" });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete role");
    }
  };

  const duplicateRole = (role: Role) => {
    setCreating(true);
    setEditingId(null);
    setNewLabel(`${role.label} (copy)`);
    setNewPermissions([...role.permissions]);
  };

  if (loading) return <LoadingState />;
  if (error && roles.length === 0) return <ErrorState message={error} />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Roles"
        subtitle="Admin always has full access. Edit User or create custom roles to fine-tune permissions."
        actions={
          !creating && !editingId ? (
            <Button
              className="bg-[#1e3a4f] hover:bg-[#163041]"
              onClick={() => {
                setCreating(true);
                setEditingId(null);
              }}
            >
              Create role
            </Button>
          ) : null
        }
      />

      {error && <p className="text-sm text-destructive">{error}</p>}

      {creating && (
        <div className="bg-white rounded-lg shadow p-4 space-y-4">
          <h3 className="font-semibold text-[#1e3a4f]">New role</h3>
          <div className="space-y-1.5 max-w-sm">
            <Label htmlFor="new-role-label">Role name</Label>
            <Input
              id="new-role-label"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="e.g. Field technician"
            />
          </div>
          <PermissionPicker selected={newPermissions} onChange={setNewPermissions} />
          <div className="flex gap-2">
            <Button
              className="bg-[#1e3a4f] hover:bg-[#163041]"
              disabled={saving || !newLabel.trim() || newPermissions.length === 0}
              onClick={() => void createRole()}
            >
              {saving ? "Creating…" : "Create role"}
            </Button>
            <Button variant="secondary" disabled={saving} onClick={() => setCreating(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {roles.map((role) => (
          <div key={role.id} className="bg-white rounded-lg shadow p-4">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
              <div>
                <h3 className="font-semibold text-[#1e3a4f]">{role.label}</h3>
                <p className="text-xs text-muted-foreground">
                  {role.id === "role_tenant_admin"
                    ? "Built-in — full access (not editable)"
                    : role.id === "role_tenant_user"
                      ? "Built-in role — rename and set permissions"
                      : "Custom role"}
                </p>
              </div>
              <div className="flex gap-2">
                {role.isEditable && editingId !== role.id && (
                  <Button size="sm" variant="secondary" onClick={() => setEditingId(role.id)}>
                    Edit
                  </Button>
                )}
                {role.isEditable && !role.isSystem && editingId !== role.id && (
                  <Button size="sm" variant="secondary" onClick={() => void deleteRole(role.id)}>
                    Delete
                  </Button>
                )}
                {role.id === "role_tenant_admin" && editingId !== role.id && (
                  <Button size="sm" variant="secondary" onClick={() => duplicateRole(role)}>
                    Duplicate
                  </Button>
                )}
              </div>
            </div>

            {editingId === role.id ? (
              <RoleEditor
                initialLabel={role.label}
                initialPermissions={role.permissions}
                labelEditable={role.id !== "role_tenant_admin"}
                saving={saving}
                onSave={(label, permissions) => void updateRole(role.id, label, permissions)}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <div className="flex flex-wrap gap-1">
                {role.permissions.map((p) => (
                  <span key={p} className="text-xs bg-muted px-2 py-0.5 rounded">
                    {PERMISSION_LABELS[p] ?? p}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
